import React, { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import WarningIcon from "@mui/icons-material/Warning";
import { useSnackbar } from "../../components/utils/SnackBar";

// ── Validation schema ──────────────────────────────────────────────────────────
// User must type "DELETE" exactly to enable the button.
const deleteSchema = Yup.object({
  confirmation: Yup.string()
    .matches(/^DELETE$/, 'Please type "DELETE" exactly to confirm')
    .required('Please type "DELETE" to confirm'),
});

// ── Props ──────────────────────────────────────────────────────────────────────
interface DeleteAccountPopupProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────
const DeleteAccountPopup: React.FC<DeleteAccountPopupProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const showSnackbar = useSnackbar();

  useEffect(() => {
    if (open) {
      showSnackbar(
        "This action cannot be undone. All your data will be permanently deleted.",
      );
    }
  }, [open, showSnackbar]);

  const handleSubmit = () => {
    onConfirm(); // parent closes the dialog and triggers the API call
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          color: "#d32f2f",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <WarningIcon /> Delete Account
      </DialogTitle>

      <Formik
        initialValues={{ confirmation: "" }}
        validationSchema={deleteSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <DialogContent sx={{ paddingY: "20px" }}>
              <Typography sx={{ marginBottom: "16px", color: "#666" }}>
                Are you sure you want to delete your account? This will:
              </Typography>

              <Box
                component="ul"
                sx={{ color: "#666", marginLeft: "20px", mb: 2 }}
              >
                <li>Delete all your courses and learning progress</li>
                <li>Remove your profile and certificates</li>
                <li>Cancel any active subscriptions</li>
                <li>Permanently erase all personal data</li>
              </Box>

              <TextField
                fullWidth
                name="confirmation"
                label='Type "DELETE" to confirm'
                margin="normal"
                className="input-root2"
                autoComplete="off"
              />
            </DialogContent>

            <DialogActions sx={{ padding: "20px" }}>
              <Button
                type="button"
                onClick={onClose}
                sx={{ color: "#999" }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="error"
                // Only active when the user has typed "DELETE" exactly
                disabled={isSubmitting || !(isValid && dirty)}
                sx={{ textTransform: "none", fontWeight: "bold" }}
              >
                Delete My Account
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default DeleteAccountPopup;
